import React, { memo, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useSelector, useDispatch } from "react-redux";
import { DisplayCard } from "./DisplayCard";
import { TotalCards } from "./TotalCards";
import { TotalPayout } from "./TotalPayout";
import { merchantsById } from "../../Merchants";
import { removeCard, updateCard } from "../thunk";
import { ConfirmCloseModal } from "./ConfirmCloseModal";
import { Icon } from "ccui-framework";
import { CardNumber } from "./CardNumber";
import { CardPin } from "./CardPin";
import { CardBalance } from "./Balance";
import { RefId } from "./Ref";
import { LOADING_EMUNS } from "../constants";
import { setLoadingState } from "../action";

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  @media (max-width: 1200px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: transparent;
  border-spacing: 0;
`;

const Row = styled.tr`
  border: 1px solid #d0d0d0;
`;

const Th = styled.th`
  text-align: center;
  padding: 6px;
  font-size: 11px;
  background: #fff;
  color: #000;
  font-weight: 700;
`;

const MobileList = styled.div`
  display: none;
  flex-direction: column;
  gap: 0.75rem;
  @media (max-width: 1200px) {
    display: flex;
  }
`;

const MobileCard = ({ card, merchants }) => {
  const dispatch = useDispatch();
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editCard, setEditCard] = useState({
    number: card.number ?? "",
    pin: card.pin ?? "",
    balance: card.enterValue + "",
    refId: card.refId ?? "",
  });
  const [editErr, setEditErr] = useState({ error: false, msg: "" });
  const myCardLoadingState = useSelector(
    (state) => state.Cart.loading.state[card.id]
  );

  useEffect(() => {
    if (isEditing && myCardLoadingState === LOADING_EMUNS.LOADED) {
      setIsEditing(false);
      dispatch(setLoadingState(card.id, LOADING_EMUNS.DEFAULT));
    }
  }, [isEditing, myCardLoadingState]);

  const merchant = merchants[card.merchant];
  if (!merchant) return null;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      currencySign: "accounting",
      style: "currency",
      currency: "USD",
    }).format(amount);

  const submitEdit = (e) => {
    e.preventDefault();
    if (!editErr.error) {
      dispatch(
        updateCard(
          {
            card: {
              merchantId: merchant.id,
              enterValue: Number(editCard.balance),
              number: editCard.number,
              pin: editCard.pin,
              refId: editCard.refId,
            },
          },
          card.id
        )
      );
    }
  };

  if (isEditing) {
    return (
      <div className="bg-white border border-[#c7a878] rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <img src={merchant.image ?? ""} alt="" style={{ height: 32 }} />
            <span className="font-semibold text-sm">{merchant.name}</span>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <CardNumber
            merchantSelectorFocus={() => {}}
            hasMerchant={true}
            defaultValue={editCard.number}
            validation={merchant.validation ?? ""}
            onChange={(_field, value, error, msg) => {
              if (error) setEditErr({ error: true, msg });
              else {
                setEditCard((c) => ({ ...c, number: value }));
                setEditErr({ error: false, msg: "" });
              }
            }}
          />
          <CardPin
            merchantSelectorFocus={() => {}}
            hasMerchant={true}
            defaultValue={editCard.pin}
            validation={merchant.validation ?? ""}
            onChange={(_field, value, error, msg) => {
              if (error) setEditErr({ error: true, msg });
              else {
                setEditCard((c) => ({ ...c, pin: value }));
                setEditErr({ error: false, msg: "" });
              }
            }}
          />
          <CardBalance
            merchantSelectorFocus={() => {}}
            hasMerchant={true}
            defaultValue={editCard.balance}
            min={merchant.limit?.min ?? 0}
            max={merchant.limit?.max ?? 0}
            onChange={(_field, value, error, msg) => {
              if (error) setEditErr({ error: true, msg });
              else {
                setEditCard((c) => ({ ...c, balance: value }));
                setEditErr({ error: false, msg: "" });
              }
            }}
          />
          <RefId
            merchantSelectorFocus={() => {}}
            hasMerchant={true}
            defaultValue={editCard.refId}
            onChange={(e) =>
              setEditCard((c) => ({ ...c, refId: e.target.value.trim() }))
            }
          />
          {editErr.error && <p className="errorMsg">{editErr.msg}</p>}
          {myCardLoadingState === LOADING_EMUNS.ERROR && (
            <p className="errorMsg">Error updating card</p>
          )}
          <div className="flex gap-2">
            <button
              className="flex-1 py-2 rounded-full border border-[#c7a878] text-[#c7a878] text-sm font-semibold"
              onClick={(e) => {
                e.preventDefault();
                setIsEditing(false);
              }}
            >
              Cancel
            </button>
            <button
              className="flex-1 py-2 rounded-full bg-[#c7a878] text-white text-sm font-semibold"
              onClick={submitEdit}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#dbdbdb] rounded-lg shadow-sm p-4">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <img src={merchant.image ?? ""} alt="" style={{ height: 32 }} />
          <span className="font-semibold text-sm">{merchant.name}</span>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            aria-label="Edit card"
            className="text-gray-500 hover:bg-gray-100 rounded px-2 py-1"
            onClick={() => setIsEditing(true)}
          >
            <Icon name="fa-edit" />
          </button>
          <button
            type="button"
            aria-label="Remove card"
            className="text-gray-500 hover:bg-gray-100 rounded px-2 py-1"
            onClick={() => setConfirmCloseOpen(true)}
          >
            <Icon name="fa-trash-alt" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-[#777984]">
        <span className="max-[500px]:col-span-2 break-all">
          <span className="font-semibold text-[#666]">Card #:</span>{" "}
          {card.number ?? "N/A"}
        </span>
        <span className="break-all">
          <span className="font-semibold text-[#666]">Pin:</span>{" "}
          {card.pin ?? "N/A"}
        </span>
        <span>
          <span className="font-semibold text-[#666]">Balance:</span>{" "}
          {formatCurrency(card.enterValue)}
        </span>
        <span>
          <span className="font-semibold text-[#666]">Sell %:</span>{" "}
          {card.percentage}%
        </span>
        <span>
          <span className="font-semibold text-[#666]">Sell Price:</span>{" "}
          {formatCurrency(card.cashValue)}
        </span>
        <span>
          <span className="font-semibold text-[#666]">Type:</span>{" "}
          {merchant.submissionMethod
            ? merchant.submissionMethod.charAt(0).toUpperCase() +
              merchant.submissionMethod.slice(1)
            : "N/A"}
        </span>
        {card.refId && <span>Ref: {card.refId}</span>}
      </div>
      <ConfirmCloseModal
        doClose={() => setConfirmCloseOpen(false)}
        isOpen={confirmCloseOpen}
        doDelete={() => dispatch(removeCard(card.id))}
      />
    </div>
  );
};

const View = memo(({ children }) => {
  const cards = useSelector((state) => state.Cart.cart.cards);
  const merchants = useSelector(merchantsById);
  const cardToDisplay = [];

  cards.forEach((card, i) => {
    cardToDisplay.push(<DisplayCard key={i} card={card} index={i} />);
  });

  if (!cards.length) return null;

  return (
    <Container>
      <TableWrapper>
        <Table>
          <thead>
            <Row>
              <Th>Merchant</Th>
              <Th>Card Number</Th>
              <Th>Pin</Th>
              <Th>RefID (Optional)</Th>
              <Th>Balance</Th>
              <Th>Sell %</Th>
              <Th>Sell Price</Th>
              <Th>Submission Type</Th>
            </Row>
          </thead>
          <tbody>{cardToDisplay}</tbody>
        </Table>
      </TableWrapper>

      <MobileList>
        {cards.map((card) => (
          <MobileCard key={card.id} card={card} merchants={merchants} />
        ))}
      </MobileList>

      <TotalCards total={cards.length} />
      <TotalPayout cards={cards} />
      {children}
    </Container>
  );
});

export { View };
